import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { LayoutDashboard, Users, LogOut, ChevronRight, Car as CarIcon, DollarSign, Settings } from 'lucide-react';
import Logo from './Logo';

const AdminLayout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();

  const handleLogout = async () => {
    await logout();
    navigate('/admin/login');
  };

  const navItems = [
    { path: '/admin', label: 'Dashboard', icon: LayoutDashboard },
    { path: '/admin/viaturas', label: 'Viaturas', icon: CarIcon },
    { path: '/admin/leads', label: 'Contactos', icon: Users },
    { path: '/admin/financas', label: 'Finanças', icon: DollarSign },
    { path: '/admin/definicoes', label: 'Definições', icon: Settings },
  ];

  const isActive = (path) => {
    if (path === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(path);
  };

  return (
    <div className="min-h-screen bg-[#090A0F] flex">
      {/* Sidebar */}
      <aside className="w-64 bg-[#12141D] border-r border-zinc-800 flex flex-col fixed h-full" data-testid="admin-sidebar">
        {/* Logo */}
        <div className="p-6 border-b border-zinc-800">
          <Link to="/" className="block" data-testid="admin-logo">
            <Logo size="default" />
          </Link>
        </div>

        {/* Navigation */}
        <nav className="flex-1 p-4">
          <ul className="space-y-2">
            {navItems.map(item => (
              <li key={item.path}>
                <Link
                  to={item.path}
                  className={`flex items-center gap-3 px-4 py-3 transition-colors ${
                    isActive(item.path)
                      ? 'bg-red-600/10 text-red-500 border-l-2 border-red-600'
                      : 'text-zinc-400 hover:text-white hover:bg-zinc-800'
                  }`}
                  data-testid={`nav-${item.label.toLowerCase()}`}
                >
                  <item.icon size={20} />
                  <span className="font-medium">{item.label}</span>
                </Link>
              </li>
            ))}
          </ul>
        </nav>

        {/* User Info & Logout */}
        <div className="p-4 border-t border-zinc-800">
          <div className="flex items-center gap-3 px-4 py-3 mb-2">
            <div className="w-10 h-10 bg-red-600 flex items-center justify-center text-white font-semibold">
              {user?.name?.charAt(0) || 'A'}
            </div>
            <div className="flex-1 min-w-0">
              <p className="text-white font-medium truncate">{user?.name || 'Admin'}</p>
              <p className="text-zinc-500 text-sm truncate">{user?.email}</p>
            </div>
          </div>
          <button
            onClick={handleLogout}
            className="w-full flex items-center gap-3 px-4 py-3 text-zinc-400 hover:text-red-500 hover:bg-zinc-800 transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={20} />
            <span className="font-medium">Terminar Sessão</span>
          </button>
        </div>
      </aside>

      {/* Main Content */}
      <main className="flex-1 ml-64">
        {/* Top Bar */}
        <header className="bg-[#12141D] border-b border-zinc-800 px-8 py-4 sticky top-0 z-10">
          <div className="flex items-center text-sm text-zinc-500">
            <Link to="/admin" className="hover:text-white transition-colors">Admin</Link>
            {location.pathname !== '/admin' && (
              <>
                <ChevronRight size={16} className="mx-2" />
                <span className="text-white">
                  {navItems.find(item => isActive(item.path))?.label || 'Página'}
                </span>
              </>
            )}
          </div>
        </header>

        {/* Page Content */}
        <div className="p-8">
          {children}
        </div>
      </main>
    </div>
  );
};

export default AdminLayout;
